import React from 'react'
import { yupResolver } from '@hookform/resolvers/yup'

import { useControlledForm, useFormReset, useModal } from 'hooks'
import { jobs } from 'orm'
import { STEP_NAMES, getCreateDefaultValues, getSteps } from './model'
import { getCreateSchema } from './schema'

const createDefaultValues = getCreateDefaultValues()

export const useFormState = setState => {
  const createSchema = getCreateSchema()

  const create = useControlledForm({
    defaultValues: createDefaultValues,
    resolver: yupResolver(createSchema),
    handleFormData: data => setState(prevState => ({ ...prevState, ...data })),
  })

  return { create }
}

export const useInternalState = (state, handlers) => {
  const [activeStep, setActiveStep] = React.useState(STEP_NAMES.SAVE_TEMPLATE)

  const timeoutId = React.useRef<number>()

  const { create, data, isOpen } = state
  const { onCancel, onClose, onConfirm } = handlers

  const {
    isOpen: isConfirmationOpen,
    handleClose: handleDoubleConfirmationClose,
    handleOpen: handleDoubleConfirmationOpen,
  } = useModal()

  const steps = getSteps()

  const activeStepIndex = steps.findIndex(({ id }) => id === activeStep)

  const isLastStep = activeStepIndex === steps.length - 1

  const handleClose = React.useCallback(handleDoubleConfirmationOpen, [
    handleDoubleConfirmationOpen,
  ])

  const handleCancel = React.useCallback(() => {
    if (isLastStep) {
      handleDoubleConfirmationOpen()

      return
    }

    onCancel()
  }, [handleDoubleConfirmationOpen, onCancel, isLastStep])

  const handleConfirm = React.useCallback(() => {
    if (isLastStep) {
      create.handleFormSubmit()

      return
    }

    setActiveStep(steps[activeStepIndex + 1].id)
  }, [activeStepIndex, create, steps, setActiveStep, isLastStep])

  const handleConfirmationClose = React.useCallback(
    handleDoubleConfirmationClose,
    [handleDoubleConfirmationClose],
  )

  const handleConfirmationConfirm = React.useCallback(() => {
    if (isLastStep) {
      create.handleFormReset()
    }

    handleDoubleConfirmationClose()
    onClose()
  }, [create, handleDoubleConfirmationClose, onClose, isLastStep])

  React.useEffect(() => {
    if (create.formState.isSubmitSuccessful) {
      onConfirm(data, {
        onFailureAction: create.handleFormApiError,
        onSuccessAction: jobs.fetchStart({
          limit: 25,
          offset: 0,
          ordering: ['-updated'],
        }),
      })
    }
  }, [create, data, onConfirm])

  React.useEffect(() => {
    if (!isOpen && activeStep !== STEP_NAMES.SAVE_TEMPLATE) {
      timeoutId.current = window.setTimeout(
        () => setActiveStep(STEP_NAMES.SAVE_TEMPLATE),
        200,
      )
    }

    return () => {
      window.clearTimeout(timeoutId.current)
    }
  }, [activeStep, setActiveStep, isOpen])

  useFormReset(
    { shouldReset: create.formState.isSubmitSuccessful },
    create.handleFormReset,
  )

  return {
    activeStep,
    handleCancel,
    handleClose,
    handleConfirm,
    handleConfirmationClose,
    handleConfirmationConfirm,
    isConfirmationOpen,
  }
}
